import { ChakraProvider } from '@chakra-ui/react';
import { addImageToFolder } from 'api/mediaFolders';
import { getInitialWidgetPosition, store } from 'modules/Editor/store';
import { selectInfographHeightPx, selectInfographWidthPx } from 'modules/Editor/store/infographSelector';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { DragEvent, ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import { theme } from 'theme';
import { generateDefaultData } from 'widgets/ImageWidget/ImageWidget.helpers';

import { UploadImagesData } from '../UploadMenu.helper';

/**
 * Define the default folder  (all uploads)
 */
export const DROPDOWN_OPTIONS = [
  {
    value: '',
    label: 'sideMenu.imagesMenu.folders.allUploads',
  },
];

/**
 * Set the number of columns for the image list on upload modal
 */
export const IMAGE_COLUMNS = 4;

/**
 * Max chars allowed to name a folder
 */
export const FOLDERS_NAME_MAX_CHARS_ALLOWED = 45;

/**
 * id of the preview used on dragging an image
 */
const DRAG_DROP_IMAGE_GHOST_ID = 'drag-drop-image-ghost-id';

export interface FolderProps {
  icon?: ReactElement;
  value?: string;
  label: string;
  medias?: string;
}

/**
 * Server returns the array as string, so we parse json here
 * @param images
 * @returns array
 */
export const getImagesFromFolder = (images: string) => {
  try {
    return images ? JSON.parse(images || '') : [];
  } catch (error) {
    console.error('Failed to parse images', error);
    return [];
  }
};

/**
 * This removes the images from folders to show on all uploads
 * @param userFolders FolderProps[] list with all users folders
 * @param uploadImages UploadImagesData list with all uploaded images
 * @returns UploadImagesData filtered list
 */
const getFilteredAll = (userFolders: FolderProps[], uploadImages: UploadImagesData): UploadImagesData => {
  const allFoldersImages = userFolders.map((f) => getImagesFromFolder(f.medias || '')).flat();
  const filteredUploadImages = uploadImages?.filter((i) => !allFoldersImages.includes(i.mediaId));

  return filteredUploadImages;
};

/**
 * Get images based on the folder that user has selected
 * @param selectedFolder selected folder by the users
 * @param uploadImages all users images
 * @param userFolders all users folder
 * @returns array
 */
export const filterByFolderImages = (
  selectedFolder: FolderProps,
  uploadImages: UploadImagesData,
  userFolders: FolderProps[],
) => {
  let folderImages = selectedFolder.value === '' ? getFilteredAll(userFolders, uploadImages) : [];
  if (selectedFolder?.medias && uploadImages && selectedFolder.value !== '') {
    const getFolderImages = getImagesFromFolder(selectedFolder.medias);
    folderImages = uploadImages.filter((i) => getFolderImages.includes(i.mediaId));
  }

  return folderImages;
};

interface onImageDropProps {
  moveToFolder: FolderProps;
  selectedImages: string[];
  selectedFolder?: FolderProps;
}

/**
 * Hook to handle when a user drops a image into a folder
 * @returns oject;
 */
export const useOnImageDrop = () => {
  const toast = useToast();
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.imagesMenu.folders',
  });

  const onImageDrop = async ({ moveToFolder, selectedFolder, selectedImages }: onImageDropProps) => {
    // prevent moving to the same folder
    if (selectedFolder?.value === moveToFolder.value) return { selectedFolder };

    // if it has a selected folder we should remove the medias from it
    if (selectedFolder?.medias) {
      const getFolderImages = getImagesFromFolder(selectedFolder.medias || '');
      const removeImagesFromFolder = getFolderImages.filter((i: string) => !selectedImages.includes(i));

      selectedFolder = {
        ...selectedFolder,
        medias: JSON.stringify(removeImagesFromFolder),
      };
    }

    // if moving to all uploads we just remove the medias from the selected folder
    if (!moveToFolder.value && selectedFolder) {
      await addImageToFolder({
        folder: {
          value: selectedFolder.value,
          medias: selectedFolder.medias,
          label: selectedFolder.label,
        },
      });

      toast({
        title: `${selectedImages.length} ${t('onMoveImages', { from: selectedFolder?.label, to: moveToFolder.label })}`,
        position: 'top',
      });

      return { selectedFolder };
    }

    // remove unused fields
    delete moveToFolder.icon;
    delete selectedFolder?.icon;

    const addMediaToNewFolder = JSON.stringify([...getImagesFromFolder(moveToFolder.medias || ''), ...selectedImages]);

    const results = await addImageToFolder({
      folder: {
        ...moveToFolder,
        medias: addMediaToNewFolder,
      },
      from: selectedFolder,
    });

    if (results.status === 'fail') {
      toast({
        title: 'Folder',
        description: 'Error on create folder',
        status: 'error',
      });
      return;
    }

    toast({
      title: `${selectedImages.length} ${t('onMoveImages', { from: selectedFolder?.label, to: moveToFolder.label })}`,
      position: 'top',
    });

    await mutate(GET_USER_MEDIA_API);

    return {
      selectedFolder,
    };
  };

  return { onImageDrop };
};

/**
 * Handler to create a preview when dragging
 * @param e drag element to be manipulated
 * @param dragImage React Element to be rendered
 * @param offset Dimensions to be used when dragging
 */
export const createDraImagePreview = (
  e: DragEvent<HTMLDivElement>,
  dragImage: JSX.Element,
  offset?: { x: number; y: number },
) => {
  // drop before creating a new one
  destroyImageGhostPreview();
  // Create ghost element
  const ghost = document.createElement('div');
  ghost.setAttribute('id', DRAG_DROP_IMAGE_GHOST_ID);

  ghost.style.position = 'absolute';
  ghost.style.overflow = 'visible';
  ghost.style.background = 'transparent';
  document.body.appendChild(ghost);
  const root = createRoot(ghost);

  const ghostCenterOffset = offset
    ? { x: offset.x, y: offset.y }
    : {
        x: 0.5,
        y: 0.5,
      };

  const image: JSX.Element = <ChakraProvider theme={theme}>{dragImage}</ChakraProvider>;

  // render ghost element to root
  root.render(image);
  // set the drag image to current drag event
  e.dataTransfer.setDragImage(ghost, ghostCenterOffset.x, ghostCenterOffset.y);
};

/**
 * Destroys ghost image preview created previously if it exists
 */
export const destroyImageGhostPreview = () => {
  const ghostImage = document.getElementById(DRAG_DROP_IMAGE_GHOST_ID);
  if (ghostImage) {
    ghostImage.remove();
  }
};

/**
 * Creates a widget data
 * @param element
 * @returns
 */
export const createWidgetData = (element: HTMLImageElement) => {
  if (!element) return;

  const infographWidthPx = selectInfographWidthPx(store.getState());
  const infographHeightPx = selectInfographHeightPx(store.getState());
  const infographSize = { width: infographWidthPx, height: infographHeightPx };
  const { top, left } = getInitialWidgetPosition();
  const { src, naturalWidth: width, naturalHeight: height } = element;

  const widget = generateDefaultData(src, { width, height }, infographSize, top, left);
  return widget;
};
